type ID = number | string

const key = (type: string, id: ID, context: string) => {
  const firstDelim = context == '' ? '' : '_'
  return `${context}${firstDelim}${type}_${id}`
}

// Keys written through this module, oldest first. localStorage enforces a hard
// per-origin quota (~5MB in Chrome) and nothing here expired, so drafts accumulated
// until a write threw and took the session with it (PES-11605). The index lets a
// full store give up our own stale entries rather than fail or touch other modules'.
const INDEX_KEY = 'persistLocally_index'

const readIndex = (): string[] => {
  try {
    const stored = localStorage.getItem(INDEX_KEY)
    const parsed = stored ? JSON.parse(stored) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const writeIndex = (keys: string[]) => {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(keys))
  } catch {
    // Best effort: without the index we only lose eviction ordering.
  }
}

const track = (storageKey: string) =>
  writeIndex([...readIndex().filter((tracked) => tracked !== storageKey), storageKey])

const untrack = (storageKey: string) => writeIndex(readIndex().filter((tracked) => tracked !== storageKey))

const isQuotaError = (error: unknown) =>
  error instanceof DOMException &&
  (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')

/** Drops the oldest entry we wrote. False once there is nothing left to give up. */
const evictOldest = (except: string) => {
  const keys = readIndex()
  const oldest = keys.find((tracked) => tracked !== except)

  if (!oldest) return false

  try {
    localStorage.removeItem(oldest)
  } catch {
    return false
  }

  writeIndex(keys.filter((tracked) => tracked !== oldest))
  return true
}

export default (type: string, context = '') => ({
  persist: (id: ID, toPersist: string) => {
    const storageKey = key(type, id, context)
    // Bounded by the number of entries we could evict, so this always terminates.
    let attempts = readIndex().length + 1

    while (attempts > 0) {
      attempts -= 1

      try {
        localStorage.setItem(storageKey, toPersist)
        track(storageKey)
        break
      } catch (error) {
        // Never throw at the caller: a lost autosave is recoverable, an exception
        // escaping into an auth or render path is not.
        if (!isQuotaError(error) || !evictOldest(storageKey)) break
      }
    }

    return toPersist
  },

  discard: (id: ID) => {
    const storageKey = key(type, id, context)
    const legacyKey = key(type, id, '') // legacy storage without context

    try {
      localStorage.removeItem(storageKey)
      localStorage.removeItem(legacyKey)
    } catch {
      // Storage is unreachable; there is nothing to discard.
    }

    untrack(storageKey)
    untrack(legacyKey)
  },

  retrieve: (id: ID) => {
    try {
      // retrieve legacy storage without context
      return localStorage.getItem(key(type, id, context)) || localStorage.getItem(key(type, id, ''))
    } catch {
      return null
    }
  },
})
